import { useCallback, useEffect, useRef } from 'react'
import { EditorState, Compartment } from '@codemirror/state'
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from '@codemirror/language'
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete'
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search'
import { useEditorStore } from '../stores/editorStore'
import { useUIStore } from '../stores/uiStore'

const lightTheme = EditorView.theme({
  '&': {
    backgroundColor: '#ffffff',
    color: '#1f2937',
    fontSize: '14px',
  },
  '.cm-content': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
    lineHeight: '1.6',
    padding: '16px 0',
  },
  '.cm-cursor': {
    borderLeftColor: '#3b82f6',
    borderLeftWidth: '2px',
  },
  '.cm-activeLine': {
    backgroundColor: '#f3f4f6',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#f3f4f6',
  },
  '.cm-gutters': {
    backgroundColor: '#f9fafb',
    borderRight: '1px solid #e5e7eb',
    color: '#9ca3af',
  },
  '.cm-selectionBackground': {
    backgroundColor: '#bfdbfe !important',
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: '#93c5fd !important',
  },
})

const darkTheme = EditorView.theme({
  '&': {
    backgroundColor: '#1e1e2e',
    color: '#cdd6f4',
    fontSize: '14px',
  },
  '.cm-content': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
    lineHeight: '1.6',
    padding: '16px 0',
  },
  '.cm-cursor': {
    borderLeftColor: '#89b4fa',
    borderLeftWidth: '2px',
  },
  '.cm-activeLine': {
    backgroundColor: '#313244',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#313244',
  },
  '.cm-gutters': {
    backgroundColor: '#181825',
    borderRight: '1px solid #313244',
    color: '#6c7086',
  },
  '.cm-selectionBackground': {
    backgroundColor: '#45475a !important',
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: '#585b70 !important',
  },
})

const themeCompartment = new Compartment()

export function useEditor(containerRef: React.RefObject<HTMLDivElement | null>) {
  const viewRef = useRef<EditorView | null>(null)
  const { setContent, setCursor, setEditorActions } = useEditorStore()
  const theme = useUIStore((s) => s.theme)
  const content = useEditorStore((s) => s.content)

  const getExtensions = useCallback(
    () => [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      history(),
      bracketMatching(),
      closeBrackets(),
      highlightSelectionMatches(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      markdown({ base: markdownLanguage, codeLanguages: languages }),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        indentWithTab,
      ]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          setContent(update.state.doc.toString())
        }
        if (update.selectionSet) {
          const pos = update.state.selection.main.head
          const line = update.state.doc.lineAt(pos)
          setCursor({
            line: line.number,
            column: pos - line.from + 1,
          })
        }
      }),
      EditorView.lineWrapping,
      themeCompartment.of(theme === 'dark' ? darkTheme : lightTheme),
    ],
    [setContent, setCursor, theme]
  )

  useEffect(() => {
    if (!containerRef.current) return

    const state = EditorState.create({
      doc: content,
      extensions: getExtensions(),
    })

    const view = new EditorView({
      state,
      parent: containerRef.current,
    })

    viewRef.current = view

    const doInsertText = (text: string) => {
      const { from, to } = view.state.selection.main
      view.dispatch({
        changes: { from, to, insert: text },
        selection: { anchor: from + text.length },
      })
      view.focus()
    }

    const doWrapSelection = (before: string, after: string) => {
      const { from, to } = view.state.selection.main
      const selected = view.state.sliceDoc(from, to)
      view.dispatch({
        changes: { from, to, insert: `${before}${selected}${after}` },
        selection: { anchor: from + before.length, head: from + before.length + selected.length },
      })
      view.focus()
    }

    setEditorActions({ insertText: doInsertText, wrapSelection: doWrapSelection })

    return () => {
      setEditorActions({ insertText: () => {}, wrapSelection: () => {} })
      view.destroy()
      viewRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!viewRef.current) return
    const currentContent = viewRef.current.state.doc.toString()
    if (currentContent !== content) {
      viewRef.current.dispatch({
        changes: {
          from: 0,
          to: currentContent.length,
          insert: content,
        },
      })
    }
  }, [content])

  useEffect(() => {
    if (!viewRef.current) return
    viewRef.current.dispatch({
      effects: themeCompartment.reconfigure(theme === 'dark' ? darkTheme : lightTheme),
    })
  }, [theme])

  const insertText = useCallback((text: string) => {
    if (!viewRef.current) return
    const view = viewRef.current
    const { from, to } = view.state.selection.main
    view.dispatch({
      changes: { from, to, insert: text },
      selection: { anchor: from + text.length },
    })
    view.focus()
  }, [])

  const wrapSelection = useCallback((before: string, after: string) => {
    if (!viewRef.current) return
    const view = viewRef.current
    const { from, to } = view.state.selection.main
    const selected = view.state.sliceDoc(from, to)
    view.dispatch({
      changes: { from, to, insert: `${before}${selected}${after}` },
      selection: { anchor: from + before.length, head: from + before.length + selected.length },
    })
    view.focus()
  }, [])

  const focus = useCallback(() => {
    viewRef.current?.focus()
  }, [])

  return { insertText, wrapSelection, focus }
}
